import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { getAccreditationFunnel } from '../../../../../../api/theHive/redeProto';
import {
  FunnelStage,
  FunnelStageCount,
  Region,
} from '../../../../../../api/theHive/redeProto/types';
import {
  FUNNEL_PIPELINE,
  FUNNEL_STAGE_LABEL,
  UF_SIGLA_BY_INT,
} from '../../../../../../constants/redeProto';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import { RedeProtoOutletContext } from '../../types';

const EMPTY_COUNTS: Record<FunnelStage, number> = {
  lead: 0,
  contacted: 0,
  registering: 0,
  docs_pending: 0,
  validating: 0,
  approved: 0,
  active: 0,
  rejected: 0,
  inactive: 0,
};

/** Todas as UFs (o filtro `uf` é server-side; não há mais lista de candidatos). */
const UF_OPTIONS = Object.values(UF_SIGLA_BY_INT).sort();

interface UseFunnelHook {
  funnel: FunnelStageCount[];
  totalLeads: number;
  activeCount: number;
  rejectedCount: number;
  inactiveCount: number;
  ufOptions: string[];
  region: Region | 'all';
  setRegion: (value: Region | 'all') => void;
  uf: string;
  setUf: (value: string) => void;
  loading: boolean;
  error: boolean;
}

export default function useFunnel(): UseFunnelHook {
  const [counts, setCounts] =
    useState<Record<FunnelStage, number>>(EMPTY_COUNTS);
  const [region, setRegion] = useState<Region | 'all'>('all');
  const [uf, setUf] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const { selectedNoticeId } = useOutletContext<RedeProtoOutletContext>();

  const getData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await getAccreditationFunnel(
        region,
        uf,
        selectedNoticeId
      );
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      if (!response.data) {
        throw new Error('Algo deu errado, tente novamente.');
      }
      setCounts(response.data);
    } catch (requestError) {
      setError(true);
      handleSnackbar(getErrorMessage(requestError), true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, uf, selectedNoticeId]);

  useEffect(() => {
    getData();
  }, [getData]);

  const funnel = useMemo<FunnelStageCount[]>(
    () =>
      FUNNEL_PIPELINE.map((stage) => ({
        stage,
        label: FUNNEL_STAGE_LABEL[stage],
        count: counts[stage] ?? 0,
      })),
    [counts]
  );

  const totalLeads = useMemo(
    () => Object.values(counts).reduce((acc, value) => acc + value, 0),
    [counts]
  );

  return {
    funnel,
    totalLeads,
    activeCount: counts.active,
    rejectedCount: counts.rejected,
    inactiveCount: counts.inactive,
    ufOptions: UF_OPTIONS,
    region,
    setRegion,
    uf,
    setUf,
    loading,
    error,
  };
}
